import {
  Component,
  EventEmitter,
  Input,
  Output,
  Inject,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';

import { Activities, IActivityEvents } from './activity.interface';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ParsedActivityDateTimePipe } from 'src/app/libs/ui/pipes/parsed-activity-date-time/parsed-activity-date-time.pipe';
import { LocalizationService } from 'src/app/shared/services/localization.service';

@Component({
  selector: 'app-activity',
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss'],
})
/* eslint no-empty-function: ["error", { "allow": ["constructors"] }]*/
export class ActivityComponent implements AfterViewInit, OnDestroy {
  currentAlphabet: string | undefined;
  readonly LANG_CONST = LANG_CONST;
  readonly CONSTANTS = CONSTANTS;
  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef;
  @Input() activities: Activities | undefined;
  @Input() entityType!: string;
  @Input() scrollTop!: boolean;
  @Input() canLoad: boolean | undefined;
  @Input() activityType!: string;
  @Input() commonAccessControl!: boolean;
  @Output() scrollDown = new EventEmitter<any>();
  activitie!: Observable<Activities>;
  scroll = false;
  updatedMonth!: string;

  expandedActivityId: string | null = null;

  // Event codes that should display description text
  private readonly DESCRIPTION_EVENT_CODES = [
    '0x07',
    '0x08',
    '0x09',
    '0x1c',
    '0x1d',
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    private parsedActivityDateTimePipe: ParsedActivityDateTimePipe,
    private localizationService: LocalizationService,
    private translate: TranslateService
  ) {
    this.localizationService.setupLocalization();
    if (data) {
      this.activities = data?.activities;
      this.entityType = data?.entityType;
      this.scroll = data?.scroll;
      this.commonAccessControl = data?.commonAccessControl;
    }
  }

  ngAfterViewInit() {
    if (this.scrollContainer && this.scrollContainer.nativeElement) {
      this.scrollContainer.nativeElement.style.maxHeight = '100%';
      this.scrollContainer.nativeElement.style.overflow = 'auto';
    }
  }

  /**
   * Resets the scroll position of the scrollable container to the top.
   */
  resetScrollPosition() {
    if (this.scrollContainer) {
      const scrollContainerElement = this.scrollContainer.nativeElement;
      scrollContainerElement.scrollTop = 0;
    }
  }

  onScrollDown(ev: any) {
    this.scrollDown.emit(ev);
  }

  /**
   * Function used as the trackBy function for ngFor directive.
   */
  trackByOption(index: number, option: any) {
    return option?.id || index;
  }

  /**
   * Determines the severity class based on event properties.
   */
  getSeverityClass(event: IActivityEvents): string {
    const eventCode = (event.eventCode || '').toLowerCase();
    const alertCodes = CONSTANTS.ALERT_DEVICE_EVENT_CODE;
    const unlockCodes = CONSTANTS.UNLOCK_DEVICE_EVENT_CODE;
    const lockedCodes = CONSTANTS.LOCKED_UNIT_EVENT_CODE;
    const onlineCodes = CONSTANTS.ONLINE_DEVICE_EVENT_CODE;

    if (
      event.isIssue === true ||
      event.isAbnormal === true ||
      alertCodes.includes(eventCode)
    ) {
      return 'severity-critical';
    } else if (unlockCodes.includes(eventCode)) {
      return 'severity-success';
    } else if (lockedCodes.includes(eventCode)) {
      return 'severity-warning';
    } else if (onlineCodes.includes(eventCode)) {
      return 'severity-info';
    }

    return 'severity-warning';
  }

  /**
   * Gets the icon path based on event code.
   */
  getEventIcon(event: IActivityEvents): string {
    const eventCode = (event.eventCode || '').toLowerCase();
    const alertCodes = CONSTANTS.ALERT_DEVICE_EVENT_CODE;
    const unlockCodes = CONSTANTS.UNLOCK_DEVICE_EVENT_CODE;
    const lockedCodes = CONSTANTS.LOCKED_UNIT_EVENT_CODE;
    const onlineCodes = CONSTANTS.ONLINE_DEVICE_EVENT_CODE;

    if (
      event.isIssue === true ||
      event.isAbnormal === true ||
      alertCodes.includes(eventCode)
    ) {
      return 'assets/svg/' + CONSTANTS.ALERT_ICON;
    } else if (unlockCodes.includes(eventCode)) {
      return 'assets/svg/' + CONSTANTS.UNLOCK_EVENT_ICON;
    } else if (lockedCodes.includes(eventCode)) {
      return 'assets/svg/' + CONSTANTS.LOCK_ICON;
    } else if (onlineCodes.includes(eventCode)) {
      return 'assets/svg/' + CONSTANTS.ONLINE_ICON;
    }

    return 'assets/svg/' + CONSTANTS.LOCK_ICON;
  }

  /**
   * Gets the source display string for an event.
   */
  getSourceDisplay(event: IActivityEvents): string {
    const rawSource = event.source || '';
    const source =
      rawSource.toUpperCase() === 'MANUAL'
        ? this.translate.instant('MANUAL')
        : rawSource;
    const sourceName = event.sourceName?.trim();

    if (sourceName) {
      if (rawSource.toUpperCase() !== 'NFC') {
        return `${source} | ${sourceName}`;
      }

      const [badgeIdPart = '', cardNamePart = ''] = sourceName
        .split('|')
        .map((part) => part.trim());
      const rawCardName = cardNamePart || sourceName;
      const cardName = event.isMaster
        ? `${this.translate.instant('MASTER')} ${rawCardName}`
        : rawCardName;
      const badgeId = badgeIdPart || '';

      return badgeId
        ? `${source} | ${cardName} - ID.${badgeId}`
        : `${source} | ${cardName}`;
    }
    return source;
  }

  /**
   * Checks if extraDescription should be shown for this event.
   */
  hasExtraDescription(event: IActivityEvents): boolean {
    const eventCode = (event.eventCode || '').toLowerCase();
    return (
      this.DESCRIPTION_EVENT_CODES.includes(eventCode) ||
      event.isIssue === true ||
      event.isAbnormal === true
    );
  }

  /**
   * Toggles the dropdown for an activity.
   */
  toggleDropdown(activityId: string): void {
    if (this.expandedActivityId === activityId) {
      this.expandedActivityId = null;
    } else {
      this.expandedActivityId = activityId;
    }
  }

  /**
   * @description: runs just before component unmount. empty the object of activities.
   */
  ngOnDestroy(): void {
    this.activities = {};
  }
}
